import { useState, useEffect, useRef } from 'react'
import { X, Mic, Speaker, Video, Settings2, Volume2, Waves, Music, Sparkles } from 'lucide-react'

interface AudioSettingsDialogProps {
  isOpen: boolean
  onClose: () => void
  // Device lists
  audioInputDevices: MediaDeviceInfo[]
  audioOutputDevices: MediaDeviceInfo[]
  videoInputDevices: MediaDeviceInfo[]
  // Selected devices
  selectedAudioInputId: string | null
  selectedAudioOutputId: string | null
  selectedVideoInputId: string | null
  // Callbacks
  onAudioInputChange: (deviceId: string) => void
  onAudioOutputChange: (deviceId: string) => void
  onVideoInputChange: (deviceId: string) => void
  // Audio processing settings
  audioSettings: AudioProcessingSettings
  onAudioSettingsChange: (settings: AudioProcessingSettings) => void
  // Video processing settings
  isBackgroundBlurEnabled?: boolean
  isBackgroundBlurLoading?: boolean
  blurStrength?: number
  onToggleBackgroundBlur?: () => void
  onBlurStrengthChange?: (strength: number) => void
  isCameraOff?: boolean
}

export interface AudioProcessingSettings {
  // High quality mode (disables processing for cleaner audio)
  highQualityMode: boolean
  // Noise suppression
  noiseSuppression: boolean
  // Noise gate (reduces low-level background noise)
  noiseGateEnabled: boolean
  noiseGateThreshold: number // -100 to 0 dB
  noiseGateAttack: number // ms (how fast gate opens)
  noiseGateRelease: number // ms (how fast gate closes)
  // Gain (microphone boost)
  gainEnabled: boolean
  gainValue: number // 0 to 2 (1 = normal)
  // Echo cancellation
  echoCancellation: boolean
  // Auto gain control
  autoGainControl: boolean
}

export const DEFAULT_AUDIO_SETTINGS: AudioProcessingSettings = {
  highQualityMode: false,
  noiseSuppression: true,
  noiseGateEnabled: false,
  noiseGateThreshold: -50,
  noiseGateAttack: 10, // 10ms attack (smooth open)
  noiseGateRelease: 150, // 150ms release (smooth close like Discord)
  gainEnabled: false,
  gainValue: 1,
  echoCancellation: true,
  autoGainControl: true,
}

export function AudioSettingsDialog({
  isOpen,
  onClose,
  audioInputDevices,
  audioOutputDevices,
  videoInputDevices,
  selectedAudioInputId,
  selectedAudioOutputId,
  selectedVideoInputId,
  onAudioInputChange,
  onAudioOutputChange,
  onVideoInputChange,
  audioSettings,
  onAudioSettingsChange,
  isBackgroundBlurEnabled = false,
  isBackgroundBlurLoading = false,
  blurStrength = 50,
  onToggleBackgroundBlur,
  onBlurStrengthChange,
  isCameraOff = false,
}: AudioSettingsDialogProps) {
  const [activeTab, setActiveTab] = useState<'devices' | 'audio' | 'video'>('devices')
  const [micLevel, setMicLevel] = useState(0)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const animationRef = useRef<number | null>(null)

  // Monitor microphone level
  useEffect(() => {
    if (!isOpen) return

    let audioContext: AudioContext | null = null
    let stream: MediaStream | null = null

    const startMonitoring = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: selectedAudioInputId
            ? { deviceId: { exact: selectedAudioInputId } }
            : true,
        })

        audioContext = new AudioContext()
        const source = audioContext.createMediaStreamSource(stream)
        const analyser = audioContext.createAnalyser()
        analyser.fftSize = 256
        source.connect(analyser)
        analyserRef.current = analyser

        const dataArray = new Uint8Array(analyser.frequencyBinCount)

        const updateLevel = () => {
          if (!analyserRef.current) return
          analyserRef.current.getByteFrequencyData(dataArray)
          const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length
          setMicLevel(average / 255)
          animationRef.current = requestAnimationFrame(updateLevel)
        }

        updateLevel()
      } catch (err) {
        console.error('Failed to start mic monitoring:', err)
      }
    }

    startMonitoring()

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
      if (audioContext) {
        audioContext.close()
      }
      if (stream) {
        stream.getTracks().forEach(track => track.stop())
      }
      analyserRef.current = null
    }
  }, [isOpen, selectedAudioInputId])

  const updateSetting = <K extends keyof AudioProcessingSettings>(
    key: K,
    value: AudioProcessingSettings[K]
  ) => {
    onAudioSettingsChange({
      ...audioSettings,
      [key]: value,
    })
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-lg bg-[var(--bg-card)] border border-[var(--border)] rounded-xl shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--border)]">
          <div className="flex items-center gap-2">
            <Settings2 size={20} className="text-[var(--primary)]" />
            <h3 className="text-lg font-semibold text-[var(--text-main)]">Settings</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-element)] rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[var(--border)]">
          <button
            onClick={() => setActiveTab('devices')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
              activeTab === 'devices'
                ? 'text-[var(--primary)] border-b-2 border-[var(--primary)]'
                : 'text-[var(--text-muted)] hover:text-[var(--text-main)]'
            }`}
          >
            Devices
          </button>
          <button
            onClick={() => setActiveTab('audio')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
              activeTab === 'audio'
                ? 'text-[var(--primary)] border-b-2 border-[var(--primary)]'
                : 'text-[var(--text-muted)] hover:text-[var(--text-main)]'
            }`}
          >
            Audio
          </button>
          <button
            onClick={() => setActiveTab('video')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
              activeTab === 'video'
                ? 'text-[var(--primary)] border-b-2 border-[var(--primary)]'
                : 'text-[var(--text-muted)] hover:text-[var(--text-main)]'
            }`}
          >
            Video
          </button>
        </div>

        {/* Content */}
        <div className="p-4 max-h-[60vh] overflow-y-auto">
          {activeTab === 'devices' ? (
            <div className="space-y-5">
              {/* Microphone */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-[var(--text-main)] mb-2">
                  <Mic size={16} />
                  Microphone
                </label>
                <select
                  value={selectedAudioInputId || ''}
                  onChange={(e) => onAudioInputChange(e.target.value)}
                  className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-3 py-2.5 rounded-lg focus:outline-none focus:border-[var(--primary)]"
                >
                  {audioInputDevices.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                    </option>
                  ))}
                </select>

                {/* Mic level meter */}
                <div className="mt-2">
                  <div className="flex items-center gap-2">
                    <Volume2 size={14} className="text-[var(--text-muted)]" />
                    <div className="flex-1 h-2 bg-[var(--bg-element)] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[var(--primary)] transition-all duration-75"
                        style={{ width: `${micLevel * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Speaker */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-[var(--text-main)] mb-2">
                  <Speaker size={16} />
                  Speaker
                </label>
                <select
                  value={selectedAudioOutputId || ''}
                  onChange={(e) => onAudioOutputChange(e.target.value)}
                  className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-3 py-2.5 rounded-lg focus:outline-none focus:border-[var(--primary)]"
                >
                  {audioOutputDevices.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label || `Speaker ${device.deviceId.slice(0, 8)}`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Camera */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-[var(--text-main)] mb-2">
                  <Video size={16} />
                  Camera
                </label>
                <select
                  value={selectedVideoInputId || ''}
                  onChange={(e) => onVideoInputChange(e.target.value)}
                  className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-3 py-2.5 rounded-lg focus:outline-none focus:border-[var(--primary)]"
                >
                  {videoInputDevices.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label || `Camera ${device.deviceId.slice(0, 8)}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : activeTab === 'audio' ? (
            <div className="space-y-5">
              {/* High Quality Mode */}
              <div className="p-4 bg-[var(--bg-element)] rounded-lg space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <Music size={20} className="text-[var(--primary)] mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-[var(--text-main)]">High Quality Audio</p>
                      <p className="text-xs text-[var(--text-muted)]">
                        Cleaner audio for music/podcasts. Disables noise processing.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      const newMode = !audioSettings.highQualityMode
                      onAudioSettingsChange({
                        ...audioSettings,
                        highQualityMode: newMode,
                        // When enabling high quality, disable processing
                        noiseSuppression: newMode ? false : true,
                        autoGainControl: newMode ? false : true,
                      })
                    }}
                    className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
                      audioSettings.highQualityMode ? 'bg-[var(--primary)]' : 'bg-[var(--bg-hover)] border border-[var(--border)]'
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                        audioSettings.highQualityMode ? 'left-[22px]' : 'left-0.5'
                      }`}
                    />
                  </button>
                </div>
                {audioSettings.highQualityMode && (
                  <p className="text-xs text-yellow-400">
                    Background noise will not be filtered. Best in quiet environments.
                  </p>
                )}
              </div>

              {/* Basic audio settings */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-[var(--text-main)] flex items-center gap-2">
                  <Waves size={16} />
                  Audio Enhancements
                </h4>

                {/* Noise Suppression */}
                <ToggleSetting
                  label="Noise Suppression"
                  description="Reduce background noise from your microphone"
                  enabled={audioSettings.noiseSuppression}
                  onChange={(v) => updateSetting('noiseSuppression', v)}
                  disabled={audioSettings.highQualityMode}
                />

                {/* Echo Cancellation */}
                <ToggleSetting
                  label="Echo Cancellation"
                  description="Prevent audio feedback loops"
                  enabled={audioSettings.echoCancellation}
                  onChange={(v) => updateSetting('echoCancellation', v)}
                />

                {/* Auto Gain Control */}
                <ToggleSetting
                  label="Auto Gain Control"
                  description="Automatically adjust microphone volume"
                  enabled={audioSettings.autoGainControl}
                  onChange={(v) => updateSetting('autoGainControl', v)}
                  disabled={audioSettings.highQualityMode}
                />
              </div>

              {/* Advanced audio settings */}
              <div className="pt-4 border-t border-[var(--border)] space-y-4">
                <h4 className="text-sm font-medium text-[var(--text-main)]">
                  Advanced Settings
                </h4>

                {/* Noise Gate */}
                <div className="space-y-2">
                  <ToggleSetting
                    label="Noise Gate"
                    description="Mute audio below threshold (reduces breathing, keyboard noise)"
                    enabled={audioSettings.noiseGateEnabled}
                    onChange={(v) => updateSetting('noiseGateEnabled', v)}
                  />

                  {audioSettings.noiseGateEnabled && (
                    <div className="ml-4 space-y-3">
                      {/* Threshold slider */}
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-xs text-[var(--text-muted)]">
                          <span>Threshold</span>
                          <span>{audioSettings.noiseGateThreshold} dB</span>
                        </div>
                        <input
                          type="range"
                          min="-100"
                          max="0"
                          step="5"
                          value={audioSettings.noiseGateThreshold}
                          onChange={(e) => updateSetting('noiseGateThreshold', Number(e.target.value))}
                          className="w-full h-2 bg-[var(--bg-element)] rounded-lg appearance-none cursor-pointer accent-[var(--primary)]"
                        />
                        <div className="flex justify-between text-xs text-[var(--text-muted)]">
                          <span>Sensitive</span>
                          <span>Aggressive</span>
                        </div>
                      </div>

                      {/* Attack slider */}
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-xs text-[var(--text-muted)]">
                          <span>Attack</span>
                          <span>{audioSettings.noiseGateAttack ?? 10}ms</span>
                        </div>
                        <input
                          type="range"
                          min="5"
                          max="50"
                          step="5"
                          value={audioSettings.noiseGateAttack ?? 10}
                          onChange={(e) => updateSetting('noiseGateAttack', Number(e.target.value))}
                          className="w-full h-2 bg-[var(--bg-element)] rounded-lg appearance-none cursor-pointer accent-[var(--primary)]"
                        />
                        <div className="flex justify-between text-xs text-[var(--text-muted)]">
                          <span>Fast</span>
                          <span>Slow</span>
                        </div>
                      </div>

                      {/* Release slider */}
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-xs text-[var(--text-muted)]">
                          <span>Release</span>
                          <span>{audioSettings.noiseGateRelease ?? 150}ms</span>
                        </div>
                        <input
                          type="range"
                          min="50"
                          max="500"
                          step="25"
                          value={audioSettings.noiseGateRelease ?? 150}
                          onChange={(e) => updateSetting('noiseGateRelease', Number(e.target.value))}
                          className="w-full h-2 bg-[var(--bg-element)] rounded-lg appearance-none cursor-pointer accent-[var(--primary)]"
                        />
                        <div className="flex justify-between text-xs text-[var(--text-muted)]">
                          <span>Quick</span>
                          <span>Smooth</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Mic Gain */}
                <div className="space-y-2">
                  <ToggleSetting
                    label="Microphone Boost"
                    description="Manually adjust microphone volume"
                    enabled={audioSettings.gainEnabled}
                    onChange={(v) => updateSetting('gainEnabled', v)}
                  />

                  {audioSettings.gainEnabled && (
                    <div className="ml-4 space-y-1">
                      <div className="flex items-center justify-between text-xs text-[var(--text-muted)]">
                        <span>Gain</span>
                        <span>{Math.round(audioSettings.gainValue * 100)}%</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="200"
                        step="10"
                        value={audioSettings.gainValue * 100}
                        onChange={(e) => updateSetting('gainValue', Number(e.target.value) / 100)}
                        className="w-full h-2 bg-[var(--bg-element)] rounded-lg appearance-none cursor-pointer accent-[var(--primary)]"
                      />
                      <div className="flex justify-between text-xs text-[var(--text-muted)]">
                        <span>0%</span>
                        <span>100%</span>
                        <span>200%</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Info note */}
              <div className="p-3 bg-[var(--bg-element)] rounded-lg">
                <p className="text-xs text-[var(--text-muted)]">
                  Note: Some settings require rejoining the meeting to take effect.
                  Advanced audio processing may increase CPU usage.
                </p>
              </div>
            </div>
          ) : activeTab === 'video' ? (
            <div className="space-y-5">
              {/* Background Blur */}
              <div className="p-4 bg-[var(--bg-element)] rounded-lg space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <Sparkles size={20} className="text-[var(--primary)] mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-[var(--text-main)]">Background Blur</p>
                      <p className="text-xs text-[var(--text-muted)]">
                        Blur your background to focus attention on you
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onToggleBackgroundBlur}
                    disabled={isCameraOff || isBackgroundBlurLoading}
                    className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
                      isBackgroundBlurEnabled ? 'bg-[var(--primary)]' : 'bg-[var(--bg-hover)] border border-[var(--border)]'
                    } ${(isCameraOff || isBackgroundBlurLoading) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div
                      className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                        isBackgroundBlurEnabled ? 'left-[22px]' : 'left-0.5'
                      }`}
                    />
                  </button>
                </div>

                {isCameraOff && (
                  <p className="text-xs text-yellow-400">
                    Turn on your camera to use background blur.
                  </p>
                )}

                {isBackgroundBlurLoading && (
                  <p className="text-xs text-[var(--text-muted)]">
                    Loading blur model... This may take a few seconds on first use.
                  </p>
                )}

                {/* Blur strength slider */}
                {isBackgroundBlurEnabled && !isCameraOff && (
                  <div className="space-y-2 pt-2 border-t border-[var(--border)]">
                    <div className="flex items-center justify-between text-xs text-[var(--text-muted)]">
                      <span>Blur Strength</span>
                      <span>{blurStrength}%</span>
                    </div>
                    <input
                      type="range"
                      min="10"
                      max="100"
                      step="5"
                      value={blurStrength}
                      onChange={(e) => onBlurStrengthChange?.(Number(e.target.value))}
                      className="w-full h-2 bg-[var(--bg-main)] rounded-lg appearance-none cursor-pointer accent-[var(--primary)]"
                    />
                    <div className="flex justify-between text-xs text-[var(--text-muted)]">
                      <span>Light</span>
                      <span>Heavy</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Info note */}
              <div className="p-3 bg-[var(--bg-element)] rounded-lg">
                <p className="text-xs text-[var(--text-muted)]">
                  Background blur uses AI to detect your outline. Performance may vary by device.
                  For best results, ensure good lighting and a clear background contrast.
                </p>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[var(--border)] flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </>
  )
}

// Toggle setting component
function ToggleSetting({
  label,
  description,
  enabled,
  onChange,
  disabled = false,
}: {
  label: string
  description: string
  enabled: boolean
  onChange: (enabled: boolean) => void
  disabled?: boolean
}) {
  return (
    <div className={`flex items-start justify-between gap-4 ${disabled ? 'opacity-50' : ''}`}>
      <div>
        <p className="text-sm text-[var(--text-main)]">{label}</p>
        <p className="text-xs text-[var(--text-muted)]">{description}</p>
      </div>
      <button
        onClick={() => !disabled && onChange(!enabled)}
        disabled={disabled}
        className={`relative w-11 h-6 rounded-full transition-colors ${
          enabled ? 'bg-[var(--primary)]' : 'bg-[var(--bg-element)] border border-[var(--border)]'
        } ${disabled ? 'cursor-not-allowed' : ''}`}
      >
        <div
          className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
            enabled ? 'left-[22px]' : 'left-0.5'
          }`}
        />
      </button>
    </div>
  )
}
